import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useId, useState, type FormEvent } from 'react';
import { setupOptions, setupVerify } from './api';
import { createPasskey, describeAuthError } from './passkeys';
import { AUTH_STATE_KEY, useAuthState } from './useAuthState';

export function SetupPage() {
  const id = useId();
  const state = useAuthState();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const setupToken = token.trim();
    if (!setupToken) return;
    setBusy(true);
    setError(null);
    try {
      const { challengeId, options } = await setupOptions(setupToken);
      // This is the Face ID / Windows Hello prompt.
      const response = await createPasskey(options);
      await setupVerify(setupToken, challengeId, response);
      await client.invalidateQueries({ queryKey: AUTH_STATE_KEY });
      await navigate({ to: '/' });
    } catch (err) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  let body;
  if (state.isPending) {
    body = (
      <section className="card" role="status" data-state="loading">
        <p className="card-title">Loading…</p>
      </section>
    );
  } else if (state.isError) {
    body = (
      <section className="card card-bad" role="status" data-state="unreachable">
        <p className="card-title">Can't reach the server</p>
        <button type="button" onClick={() => void state.refetch()}>
          Try again
        </button>
      </section>
    );
  } else if (!state.data.setupOpen) {
    body = (
      <section className="card" data-state="closed">
        <h2 className="card-heading">Already set up</h2>
        <p className="card-detail">Personal OS already has an owner, so setup is closed.</p>
        <Link to="/sign-in">Go to sign-in</Link>
      </section>
    );
  } else {
    body = (
      <section className="card" data-state="open" aria-labelledby={`${id}-title`}>
        <h2 id={`${id}-title`} className="card-heading">
          Set up Personal OS
        </h2>
        <p className="card-detail">
          Enter the setup token you were given. Then you'll create a passkey, which means signing in
          with Face ID (or Windows Hello) from now on.
        </p>
        <form onSubmit={(e) => void submit(e)} className="stack">
          <label htmlFor={id}>Setup token</label>
          <input
            id={id}
            type="password"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
          {error && (
            <p role="alert" className="error-text">
              {error}
            </p>
          )}
          <button type="submit" className="primary" disabled={busy || token.trim() === ''}>
            {busy ? 'Waiting…' : 'Create my passkey'}
          </button>
        </form>
      </section>
    );
  }

  return (
    <main className="page">
      <h1>Personal OS</h1>
      {body}
    </main>
  );
}
